import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { FormField, MoneyField, DropdownField } from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import {
  calcNetProfit,
  generateId,
  useApp,
  type Sale,
  type SaleVenue,
} from "@/context/AppContext";

export const Route = createFileRoute("/sales/create")({
  component: CreateSalePage,
  validateSearch: (
    search,
  ): {
    productId?: string;
    cardId?: string;
    itemType?: string;
    venue?: string;
    salePrice?: string;
  } => ({
    productId:
      typeof search.productId === "string" ? search.productId : undefined,
    cardId: typeof search.cardId === "string" ? search.cardId : undefined,
    itemType: typeof search.itemType === "string" ? search.itemType : undefined,
    venue: typeof search.venue === "string" ? search.venue : undefined,
    salePrice:
      typeof search.salePrice === "string" ? search.salePrice : undefined,
  }),
});

const VENUES: SaleVenue[] = [
  "eBay",
  "Facebook Marketplace",
  "Local",
  "TCGplayer",
  "Website",
  "Other",
];

function CreateSalePage() {
  const navigate = useNavigate();
  const params = Route.useSearch();
  const { cards, products, lots, liveListings, addSale, completeSale } =
    useApp();

  const prefillItemType: "Card" | "Product" = params.productId
    ? "Product"
    : params.cardId
      ? "Card"
      : params.itemType === "Card"
        ? "Card"
        : "Product";
  const prefillItemId = params.productId ?? params.cardId ?? "";
  const prefillVenue = (params.venue as SaleVenue | undefined) ?? "eBay";
  const prefillProduct = params.productId
    ? products.find((p) => p.id === params.productId)
    : undefined;
  const prefillCard = params.cardId
    ? cards.find((c) => c.id === params.cardId)
    : undefined;
  const prefillSourceLotId =
    prefillProduct?.sourceLotId ?? prefillCard?.sourceLotId ?? "";
  const prefillPackaging = prefillProduct?.packagingCost ?? 0;
  const prefillLiveListing = prefillProduct
    ? liveListings.find((l) => l.productId === prefillProduct.id)
    : undefined;
  const prefillSalePrice =
    params.salePrice ??
    (prefillLiveListing
      ? String(prefillLiveListing.askingPrice)
      : prefillProduct && prefillProduct.targetSalePrice > 0
        ? String(prefillProduct.targetSalePrice)
        : prefillCard && prefillCard.expectedSalePrice > 0
          ? String(prefillCard.expectedSalePrice)
          : "");

  const [itemType, setItemType] = useState<"Card" | "Product">(prefillItemType);
  const [itemId, setItemId] = useState(prefillItemId);
  const [venue, setVenue] = useState<SaleVenue>(prefillVenue);
  const [saleDate, setSaleDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [salePrice, setSalePrice] = useState(prefillSalePrice);
  const [buyerShipping, setBuyerShipping] = useState("");
  const [actualShipping, setActualShipping] = useState("");
  const [platformFees, setPlatformFees] = useState("");
  const [paymentFees, setPaymentFees] = useState("");
  const [packagingCost, setPackagingCost] = useState(
    prefillPackaging > 0 ? String(prefillPackaging) : "",
  );
  const [sourceLotId, setSourceLotId] = useState(prefillSourceLotId);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [shipped, setShipped] = useState("No");
  const [saving, setSaving] = useState(false);
  const [salePriceError, setSalePriceError] = useState("");
  const [saleDateError, setSaleDateError] = useState("");

  const itemOptions = useMemo(
    () =>
      itemType === "Card"
        ? cards.map((c) => ({ id: c.id, label: `${c.name} (${c.id})` }))
        : products.map((p) => ({
            id: p.id,
            label: `${p.name || p.productType} (${p.id})`,
          })),
    [itemType, cards, products],
  );

  const lotOptions = lots.map((l) => l.id);
  const lotSelectOptions = useMemo(
    () => [
      { value: "", label: "— No lot —" },
      ...[...lots]
        .sort(
          (a, b) =>
            new Date(b.purchaseDate).getTime() -
            new Date(a.purchaseDate).getTime(),
        )
        .map((l) => {
          const date = new Date(l.purchaseDate).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          return {
            value: l.id,
            label: l.seller ? `${l.seller} · ${date}` : date,
          };
        }),
    ],
    [lots],
  );

  const selectedItem = useMemo(
    () =>
      itemType === "Card"
        ? cards.find((c) => c.id === itemId)
        : products.find((p) => p.id === itemId),
    [itemType, itemId, cards, products],
  );

  const itemName =
    itemType === "Card"
      ? ((selectedItem as { name?: string } | undefined)?.name ?? itemId)
      : ((selectedItem as { name?: string; productType?: string } | undefined)
          ?.name ??
        (selectedItem as { productType?: string } | undefined)?.productType ??
        itemId);

  const previewSale = {
    salePrice: parseFloat(salePrice) || 0,
    buyerShipping: parseFloat(buyerShipping) || 0,
    actualShipping: parseFloat(actualShipping) || 0,
    platformFees: parseFloat(platformFees) || 0,
    paymentFees: parseFloat(paymentFees) || 0,
    packagingCost: parseFloat(packagingCost) || 0,
  } as Sale;

  const net = calcNetProfit(previewSale);

  const autoFillFees = () => {
    const price = parseFloat(salePrice) || 0;
    if (venue === "eBay") {
      setPlatformFees((price * 0.1325).toFixed(2));
      setPaymentFees((price * 0.029 + 0.3).toFixed(2));
    } else if (venue === "TCGplayer") {
      setPlatformFees((price * 0.1099).toFixed(2));
      setPaymentFees((price * 0.029 + 0.3).toFixed(2));
    } else if (venue === "Facebook Marketplace") {
      setPlatformFees((price * 0.05).toFixed(2));
    } else if (venue === "Website") {
      // Direct/website sale — no marketplace cut. Owner adds a payment-
      // processor fee manually if they collected by card.
      setPlatformFees("0.00");
    }
  };

  const isValidDate = (s: string) =>
    /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(Date.parse(s));

  const handleSave = () => {
    let valid = true;
    if (
      !salePrice.trim() ||
      isNaN(parseFloat(salePrice)) ||
      parseFloat(salePrice) < 0
    ) {
      setSalePriceError(
        !salePrice.trim()
          ? "Enter a sale price"
          : "Enter a valid non-negative amount",
      );
      valid = false;
    } else {
      setSalePriceError("");
    }
    if (!isValidDate(saleDate)) {
      setSaleDateError("Enter a valid date (YYYY-MM-DD)");
      valid = false;
    } else {
      setSaleDateError("");
    }
    if (!valid) return;

    setSaving(true);
    const sale: Sale = {
      id: generateId("SALE"),
      saleDate,
      itemType,
      itemId,
      itemName,
      venue,
      salePrice: parseFloat(salePrice) || 0,
      buyerShipping: parseFloat(buyerShipping) || 0,
      actualShipping: parseFloat(actualShipping) || 0,
      platformFees: parseFloat(platformFees) || 0,
      paymentFees: parseFloat(paymentFees) || 0,
      packagingCost: parseFloat(packagingCost) || 0,
      sourceLotId,
      trackingNumber,
      shipped: shipped === "Yes",
      createdAt: new Date().toISOString(),
    };
    if (itemType === "Product" && itemId) {
      // Manual-log path: prefer matching an Active listing so cross-listing
      // siblings get auto-superseded too. If none matches, fall through
      // with empty listingId (server treats as a manual sale — no supersede).
      const matchingListing = liveListings.find(
        (l) => l.productId === itemId && (l.status ?? "Active") === "Active",
      );
      // Fire-and-forget the supersede summary; this form doesn't render a
      // toast for it. Future enhancement: surface the count in a banner.
      void completeSale(itemId, matchingListing?.id ?? "", sale);
    } else {
      addSale(sale);
    }
    setSaving(false);
    navigate({ to: "/sales" });
  };

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <Link
          to="/sales"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Cancel
        </Link>
      </div>

      <div className="flex-1 space-y-1 overflow-y-auto py-4 pr-2">
        <h1 className="mb-3 text-xl font-bold text-foreground">Log Sale</h1>

        {/* Calc card */}
        <div className="mb-3 rounded-md bg-primary p-3.5 text-primary-foreground">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col items-center gap-0.5">
              <div className="text-2xl font-bold text-secondary">
                ${(parseFloat(salePrice) || 0).toFixed(2)}
              </div>
              <div className="text-[11px] opacity-60">Sale</div>
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <div
                className={`text-2xl font-bold ${
                  net >= 0 ? "text-emerald-400" : "text-red-400"
                }`}
              >
                ${net.toFixed(2)}
              </div>
              <div className="text-[11px] opacity-60">Net Profit</div>
            </div>
          </div>
        </div>

        <DropdownField
          label="Item Type"
          value={itemType}
          options={["Card", "Product"]}
          onChange={(v) => {
            setItemType(v as "Card" | "Product");
            setItemId("");
          }}
        />
        {itemOptions.length > 0 ? (
          <DropdownField
            label={`Select ${itemType}`}
            value={itemId}
            options={[
              { value: "", label: `— Select ${itemType} —` },
              ...itemOptions.map((o) => ({ value: o.id, label: o.label })),
            ]}
            onChange={setItemId}
          />
        ) : (
          <FormField
            label={`${itemType} ID`}
            value={itemId}
            onChangeText={setItemId}
            placeholder="Enter ID manually"
          />
        )}
        {!itemId && (
          <FormField
            label="Item Description"
            value={itemName}
            onChangeText={() => {}}
            placeholder="Auto-filled from selection"
          />
        )}
        <DropdownField
          label="Venue"
          value={venue}
          options={VENUES}
          onChange={(v) => setVenue(v as SaleVenue)}
        />
        <FormField
          label="Sale Date"
          type="date"
          value={saleDate}
          onChangeText={(v) => {
            setSaleDate(v);
            if (/^\d{4}-\d{2}-\d{2}$/.test(v) && !isNaN(Date.parse(v))) {
              setSaleDateError("");
            }
          }}
          error={saleDateError}
        />
        <MoneyField
          label="Sale Price"
          value={salePrice}
          onChangeText={(v) => {
            setSalePrice(v);
            if (v.trim() && !isNaN(parseFloat(v)) && parseFloat(v) >= 0) {
              setSalePriceError("");
            }
          }}
          required
          error={salePriceError}
        />
        <MoneyField
          label="Buyer Paid Shipping"
          value={buyerShipping}
          onChangeText={setBuyerShipping}
        />

        <PrimaryButton
          label={`Auto-Fill ${venue} Fees`}
          onClick={autoFillFees}
          variant="secondary"
        />
        <div className="h-1" />

        <MoneyField
          label="Platform Fees"
          value={platformFees}
          onChangeText={setPlatformFees}
        />
        <MoneyField
          label="Payment Fees"
          value={paymentFees}
          onChangeText={setPaymentFees}
        />
        <MoneyField
          label="Actual Shipping Cost"
          value={actualShipping}
          onChangeText={setActualShipping}
        />
        <MoneyField
          label="Packaging Cost"
          value={packagingCost}
          onChangeText={setPackagingCost}
        />
        {lotOptions.length > 0 && (
          <DropdownField
            label="Source Lot"
            value={sourceLotId}
            options={lotSelectOptions}
            onChange={setSourceLotId}
          />
        )}
        <FormField
          label="Tracking Number"
          value={trackingNumber}
          onChangeText={setTrackingNumber}
          placeholder="Optional"
        />
        <DropdownField
          label="Shipped?"
          value={shipped}
          options={["Yes", "No"]}
          onChange={setShipped}
        />

        <div className="pt-2">
          <PrimaryButton
            label="Log Sale"
            onClick={handleSave}
            loading={saving}
          />
        </div>
      </div>
    </section>
  );
}
